<template lang="pug">
.podio
  header.podio__hero
    .podio__hero-grid
    .podio__hero-glow.podio__hero-glow--blue
    .podio__hero-glow.podio__hero-glow--red
    .podio__hero-inner
      p.podio__eyebrow
        span.podio__eyebrow-pip
        | Resultado final
      h1.podio__title Pódio
      p.podio__sub(v-if="getCurrentEvent") Classificação de {{ getCurrentEvent.name }}, por categoria.
      p.podio__sub(v-else) Escolha um evento para ver a classificação.

      .podio__hero-actions(v-if="temAlgumEncerrado")
        button.podio__export(type="button" @click="doExport")
          FontAwesomeIcon(:icon="['fas', 'file-download']")
          | Exportar classificação

  .podio__wrap
    p.podio__loading(v-if="carregando")
      FontAwesomeIcon(:icon="['fas', 'circle-notch']" spin)
      | Apurando resultados...

    .podio__empty(v-else-if="!categorias.length")
      span.podio__empty-ic 🏆
      h3.podio__empty-title Nada apurado ainda
      p.podio__empty-text Este evento não tem categoria com chave encerrada. O pódio aparece quando a decisão de cada categoria termina.

    template(v-else)
      section.cat(v-for="cat in categorias" :key="cat.category_id")
        .cat__head
          h2.cat__name {{ cat.category_name }}
          span.cat__count {{ cat.standings.length }} robôs

        //- ---------- PÓDIO ----------
        .pod
          .pod__place(
            v-for="lugar in podio(cat)"
            :key="lugar.position"
            :class="'pod__place--' + lugar.position"
          )
            span.pod__medal {{ medalha(lugar.position) }}
            span.pod__pos {{ lugar.position }}º
            span.pod__robot {{ lugar.robot_name }}
            span.pod__team {{ lugar.team_name || 'Sem equipe' }}

        //- ---------- CLASSIFICAÇÃO COMPLETA ----------
        .cat__table-wrap
          table.tbl
            thead
              tr
                th.tbl__pos Pos.
                th Robô
                th Equipe
                th.tbl__where Eliminado em
            tbody
              tr(
                v-for="linha in cat.standings"
                :key="linha.robot_id"
                :class="{ 'tbl__row--pod': linha.position <= 3 }"
              )
                td.tbl__pos
                  span.tbl__medal(v-if="linha.position <= 3") {{ medalha(linha.position) }}
                  | {{ linha.position }}º
                  span.tbl__tied(v-if="linha.tied" title="Outros robôs dividem esta posição") =
                td.tbl__robot {{ linha.robot_name }}
                td {{ linha.team_name || 'Sem equipe' }}
                td.tbl__where {{ linha.eliminated_in }}

      p.podio__nota(v-if="pendentes.length")
        FontAwesomeIcon(:icon="['fas', 'info-circle']")
        | Ainda sem resultado: {{ pendentes.join(', ') }}. A classificação aparece quando a decisão da categoria termina.
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import axios from '@/axios'

export default {
  name: 'Podio',
  data: () => ({
    todas: [],
    carregando: true
  }),
  created () {
    this.setEvents().then(() => this.carregar())
  },
  computed: {
    ...mapGetters('events', ['getCurrentEvent', 'getCurrentEventId']),
    // Só categoria encerrada entra: pódio de chave em andamento seria mentira.
    categorias () {
      return this.todas.filter(c => c.finished && c.standings.length)
    },
    pendentes () {
      return this.todas.filter(c => !c.finished).map(c => c.category_name)
    },
    temAlgumEncerrado () {
      return this.categorias.length > 0
    }
  },
  methods: {
    ...mapActions('events', ['setEvents']),
    carregar () {
      const id = this.getCurrentEventId
      // Limpa antes de buscar: sem isto, trocar para um evento sem resultado
      // deixaria na tela a classificação do evento anterior.
      this.todas = []
      if (!id) { this.carregando = false; return }
      return axios.standings.finalStandings(id).then((resp) => {
        this.todas = Array.isArray(resp) ? resp : []
      }).catch(() => {
        this.todas = []
      }).finally(() => {
        this.carregando = false
      })
    },
    medalha (pos) {
      return { 1: '🥇', 2: '🥈', 3: '🥉' }[pos] || ''
    },
    // O pódio mostra as três primeiras COLOCAÇÕES, não os três primeiros robôs:
    // com empate em 3º, os dois aparecem.
    podio (cat) {
      return cat.standings.filter(l => l.position <= 3)
    },
    // Mesmo formato do export de robôs aprovados: ";" e BOM, que é o que o Excel
    // em pt-BR abre sem quebrar acento nem jogar tudo numa coluna.
    doExport () {
      const escape = (v) => {
        const s = String(v === null || v === undefined ? '' : v).trim()
        return /[";\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
      }
      const linhas = [['Categoria', 'Posição', 'Robô', 'Equipe', 'Eliminado em']]
      for (const cat of this.categorias) {
        for (const l of cat.standings) {
          linhas.push([cat.category_name, l.position + 'º', l.robot_name, l.team_name || 'Sem equipe', l.eliminated_in])
        }
      }
      const csv = '﻿' + linhas.map(l => l.map(escape).join(';')).join('\r\n')

      const evento = (this.getCurrentEvent && this.getCurrentEvent.name) || 'evento'
      const slug = evento.normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `classificacao-${slug}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  },
  watch: {
    getCurrentEventId () {
      this.carregando = true
      this.carregar()
    }
  }
}
</script>

<style lang="sass" scoped>
  $blue: #287AED
  $red: #DD3434
  $gold: #C8991A
  $ink: #0B0E14
  $muted: #5C6673
  $line: rgba(11, 14, 20, 0.10)
  $paper: #FFFFFF
  $page: #F2F4F8
  $display: 'Saira Condensed', 'Lato', sans-serif
  $mono: 'Space Mono', 'Lato', monospace

  .podio
    color: $ink
    background: $page
    min-height: calc(100vh - 64px)

    *
      box-sizing: border-box

    &__hero
      position: relative
      overflow: hidden
      isolation: isolate
      padding: 26px 0 40px
      background: radial-gradient(circle at 16% 8%, #15233b 0%, $ink 52%, #05070b 100%)
      color: #fff

    &__hero-grid
      position: absolute
      inset: 0
      background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      background-size: 42px 42px
      mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
      -webkit-mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
      z-index: -2

    &__hero-glow
      position: absolute
      width: 360px
      height: 360px
      border-radius: 50%
      filter: blur(110px)
      opacity: 0.5
      z-index: -1
      &--blue
        background: $blue
        top: -170px
        left: -90px
      &--red
        background: $red
        bottom: -190px
        right: -70px

    &__hero-inner
      position: relative
      max-width: 1140px
      margin: 0 auto
      padding: 0 24px

    &__eyebrow
      display: inline-flex
      align-items: center
      gap: 10px
      font-family: $mono
      text-transform: uppercase
      letter-spacing: 0.22em
      font-size: 12px
      font-weight: 700
      color: rgba(255, 255, 255, 0.7)
      margin: 0 0 12px

    &__eyebrow-pip
      width: 8px
      height: 8px
      border-radius: 50%
      background: linear-gradient(135deg, #FFD86B, $gold)
      box-shadow: 0 0 0 4px rgba(255, 216, 107, 0.15)

    &__title
      font-family: $display
      font-size: 42px
      line-height: 1.02
      font-weight: 800
      letter-spacing: -0.01em
      margin: 0

    &__sub
      max-width: 620px
      margin: 12px 0 0
      font-size: 15px
      line-height: 1.5
      color: rgba(255, 255, 255, 0.72)

    &__hero-actions
      margin-top: 20px

    &__export
      display: inline-flex
      align-items: center
      gap: 9px
      padding: 9px 18px
      border-radius: 999px
      border: 1px solid rgba(255, 255, 255, 0.22)
      background: rgba(255, 255, 255, 0.10)
      color: #fff
      font-family: $display
      font-size: 15px
      font-weight: 700
      cursor: pointer
      transition: background .18s ease, transform .18s ease
      &:hover
        background: rgba(255, 255, 255, 0.2)
        transform: translateY(-1px)

    &__wrap
      max-width: 1140px
      margin: 0 auto
      padding: 32px 24px 64px

    &__loading
      display: flex
      align-items: center
      gap: 10px
      margin: 0
      padding: 22px
      background: $paper
      border: 1px solid $line
      border-radius: 18px
      color: $muted

    &__empty
      display: flex
      flex-direction: column
      align-items: center
      text-align: center
      gap: 8px
      padding: 56px 24px
      background: $paper
      border: 1px solid $line
      border-radius: 18px
      &-ic
        font-size: 40px
      &-title
        font-family: $display
        font-size: 22px
        margin: 6px 0 0
      &-text
        max-width: 460px
        margin: 0
        font-size: 14px
        line-height: 1.5
        color: $muted

    &__nota
      display: flex
      align-items: flex-start
      gap: 9px
      margin: 4px 0 0
      padding: 13px 16px
      border-radius: 12px
      background: rgba(40, 122, 237, 0.07)
      border: 1px solid rgba(40, 122, 237, 0.2)
      color: $muted
      font-size: 13px
      line-height: 1.5

  // ---------- CATEGORIA ----------
  .cat
    background: $paper
    border: 1px solid $line
    border-radius: 18px
    padding: 20px 22px 8px
    margin-bottom: 22px
    box-shadow: 0 6px 20px rgba(11, 14, 20, 0.06)

    &__head
      display: flex
      align-items: baseline
      justify-content: space-between
      gap: 14px
      flex-wrap: wrap
      margin-bottom: 18px

    &__name
      font-family: $display
      font-size: 25px
      font-weight: 800
      margin: 0

    &__count
      font-family: $mono
      font-size: 11px
      letter-spacing: 0.06em
      color: $muted

    &__table-wrap
      overflow-x: auto
      margin: 0 -22px
      padding: 0 22px

  // ---------- PÓDIO ----------
  .pod
    display: grid
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr))
    gap: 12px
    margin-bottom: 22px

    &__place
      display: flex
      flex-direction: column
      align-items: center
      text-align: center
      gap: 3px
      padding: 18px 14px
      border-radius: 14px
      border: 1px solid $line
      background: #FBFCFE
      &--1
        border-color: rgba(200, 153, 26, 0.5)
        background: linear-gradient(180deg, rgba(255, 216, 107, 0.16), rgba(255, 216, 107, 0.03))
      &--2
        border-color: rgba(140, 150, 165, 0.45)
        background: linear-gradient(180deg, rgba(150, 160, 175, 0.14), rgba(150, 160, 175, 0.02))
      &--3
        border-color: rgba(176, 110, 60, 0.4)
        background: linear-gradient(180deg, rgba(196, 124, 66, 0.13), rgba(196, 124, 66, 0.02))

    &__medal
      font-size: 30px
      line-height: 1

    &__pos
      font-family: $mono
      font-size: 11px
      font-weight: 700
      letter-spacing: 0.1em
      color: $muted

    &__robot
      font-family: $display
      font-size: 20px
      font-weight: 800
      line-height: 1.15
      word-break: break-word

    &__team
      font-size: 13px
      color: $muted

  // ---------- TABELA ----------
  .tbl
    width: 100%
    border-collapse: collapse
    font-size: 14px

    th
      text-align: left
      padding: 10px 12px
      border-bottom: 1px solid $line
      font-family: $mono
      font-size: 10px
      text-transform: uppercase
      letter-spacing: 0.1em
      color: $muted
      white-space: nowrap

    td
      padding: 11px 12px
      border-bottom: 1px solid rgba(11, 14, 20, 0.055)

    tbody tr:last-child td
      border-bottom: none

    &__row--pod td
      background: rgba(255, 216, 107, 0.05)

    &__pos
      width: 92px
      white-space: nowrap
      font-family: $mono
      font-weight: 700

    &__medal
      margin-right: 5px

    &__tied
      margin-left: 4px
      color: $muted
      font-weight: 400

    &__robot
      font-weight: 600

    &__where
      color: $muted
      white-space: nowrap
</style>
